import { Button, Card, Col, DatePicker, Input, Row } from 'antd';
import React, { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { SearchOutlined } from '@ant-design/icons';
import { RootState } from '../../../../store';
import { ReconciliationState } from '../../../../store/models/reconciliationModel';
import { getReconciliationData, setReconciliationStartIndex, updateReconciliationColumnFilters } from '../../../../store/actions/reconciliationAction';
import AmountRangeSelector from '../../../../shared/components/AmountRangeSelector/AmountRangeSelector';
import MultiselectDropdown from '../../../../shared/components/MultiselectDropdown/MultiselectDropdown';
import { GUTTOR_SPACE_SMALL } from '../../../../shared/constants/commonConstants';
import ApplicationDetail from '../../../../shared/services/applicationDetail.service';
import { replaceRsAndComma } from '../../../../utils/utility';

const FilterDropdownComponent = (props: any): ReactElement => {
  const { filterKey, inputType, placeholder, confirm,
    optionPath, labelKey = '', additionalLabelKey, additionalLabelKeyDivider,
    optionsFilterKey = null } = props;
  const dispatch = useDispatch();
  const { insurerName, tpaId, centerId : centerID, utrDate, dod, utrNumber, searchKey, columnFilters: selectedFilters, viewId,
    sortingKey, statusFilters, startIndex, range} = useSelector((state: RootState): ReconciliationState => state.reconciliation || []);
  const rootState = useSelector((state: RootState): RootState => state || {});
  const { RangePicker } = DatePicker;
  const appDetail = ApplicationDetail.instance;

  const onSearch = (): any => {
    dispatch(setReconciliationStartIndex(1));
    const payload = {
      providerId: appDetail.getProviderId(),
      insurerName,
      tpaId,
      centerID,
      utrDate,
      dod,
      utrNumber,
      searchKey,
      viewId,
      filters: selectedFilters,
      statusFilters,
      sortingKey,
      startIndex,
      range,
    };
    dispatch(getReconciliationData(payload));
    confirm();
  };

  const inputChangeHandler = (event: any): void => {
    const filtersList = selectedFilters;
    let keyFound = false;
    // update the value if key exists
    filtersList.forEach((el: any, index: any): any => {
      if (el.key === filterKey) {
        keyFound = true;
        if (event.target.value === '') {
          filtersList.splice(index, 1);
        } else {
          el.value = [event.target.value];
        }
      }
    });
    // push the obj if key not found
    if (!keyFound && event.target.value !== '') {
      const obj = {key: filterKey, value: [event.target.value]};
      filtersList.push(obj);
    }
    dispatch(updateReconciliationColumnFilters(filtersList));
    if (event.target.value === '') {
      onSearch();
    }
  };

  const dateOnChange = (event: any): any => {
    let keyFound = false;
    const filtersList = selectedFilters;
    filtersList.forEach((el: any, index: any): any => {
      if (el.key === filterKey) {
        keyFound = true;
        if (event === null) {
          filtersList.splice(index, 1);
        } else {
          el.value = event;
        }
      }
    });
    if (!keyFound && event !== null) {
      const obj = {key: filterKey, value: event};
      filtersList.push(obj);
    }
    dispatch(updateReconciliationColumnFilters(filtersList));
    if (event === null) {
      onSearch();
    }
  };

  const onAmountChange = (value: any, position: number): any => {
    let keyFound = false;
    let updatedEvent = [];
    const filtersList = selectedFilters;
    filtersList.forEach((el: any, index: any): any => {
      if (el.key === filterKey) {
        keyFound = true;
        if (value === '') {
          filtersList.splice(index, 1);
        } else {
          updatedEvent = [...el.value];
          updatedEvent[position] = replaceRsAndComma(value);
          el.value = updatedEvent;
        }
      }
    });
    if (!keyFound && value !== '') {
      updatedEvent[position] = replaceRsAndComma(value);
      const obj = {key: filterKey, value: updatedEvent};
      filtersList.push(obj);
    }
    dispatch(updateReconciliationColumnFilters(filtersList));
    if (value === '') {
      onSearch();
    }
  };

  const onMultiselectDropdownChange = (value: any): any => {
    let keyFound = false;
    const filtersList = selectedFilters;
    filtersList.forEach((el: any, index: any): any => {
      if (el.key === filterKey) {
        keyFound = true;
        if (value.length === 0) {
          filtersList.splice(index, 1);
        } else {
          el.value = value;
        }
      }
    });
    if (!keyFound && value !== '') {
      const obj = {key: filterKey, value};
      filtersList.push(obj);
    }
    dispatch(updateReconciliationColumnFilters(filtersList));
    if (value.length === 0) {
      onSearch();
    }
  };

  const getAvailableOptionsFromState = (state: RootState): any => {
    const availableOptions = Object.assign(get(state, optionPath));
    const updatedAvailableOptions = availableOptions.filter((availableOption: any): any => {
      if (optionsFilterKey) { return availableOption[optionsFilterKey]; } return availableOption; })
      .map((availableOption: any): any => {
      availableOption.key = availableOption.key;
      if (additionalLabelKey && availableOption[labelKey]) {
        const additionalData =
          availableOption[additionalLabelKey]?.length > 0
          ? `${additionalLabelKeyDivider} ${availableOption[additionalLabelKey]}`
          : '';
        availableOption.value = `${availableOption[labelKey]} ${additionalData}`;
      } if (labelKey && availableOption[labelKey]) {
        availableOption.value = availableOption[labelKey];
      }
      return {key: availableOption.key, value: availableOption.value};
    });
    return updatedAvailableOptions;
  };

  function getValue(filter: any): any {
    if (filter.key === filterKey) {
      return filter;
    }
  }

  const getInputField = (): ReactElement | null => {
    switch (inputType) {
      case 'TEXT':
        return (
          <Row className="custom-amount-range">
            <Col>
              <Input
                placeholder={placeholder}
                allowClear={true}
                size="middle"
                onChange={inputChangeHandler}
                value={selectedFilters.filter(getValue)[0]?.value}
                data-testid={filterKey}
                className="input-field"
                onPressEnter={onSearch}
              />
            </Col>
            <Col md={2}>
              <Button type="primary" icon={<SearchOutlined />} className="search-btn" onClick={onSearch} />
            </Col>
          </Row>
        );
      case 'DATE_RANGE':
        return (
          <Row className="custom-amount-range">
            <Col md={20} className="text-input">
              <RangePicker
                allowClear={true}
                onChange={dateOnChange}
                value={selectedFilters.filter(getValue)[0]?.value as any}
                className="no-right-border-radius-rangepicker"
              />
            </Col>
            <Col md={4}>
              <Button type="primary" icon={<SearchOutlined />} className="w-100" onClick={onSearch}/>
            </Col>
          </Row>
        );
      case 'AMOUNT':
        return (
          <AmountRangeSelector
            value={selectedFilters.filter(getValue)[0]?.value}
            onChange={onAmountChange}
            onSearch={onSearch}
          />
        );
      case 'MULTISELECT_DROPDOWN':
        return (
          <MultiselectDropdown
            placeholder={placeholder}
            options={getAvailableOptionsFromState(rootState)}
            value={selectedFilters.filter(getValue)[0]?.value}
            onChange={onMultiselectDropdownChange}
            onSearch={onSearch}
            key={selectedFilters.filter(getValue)[0]?.key}
          />
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <Card className="in-patient-filter-dropdown">
        <Row gutter={GUTTOR_SPACE_SMALL}>
          <Col md={24}>
            {getInputField()}
          </Col>
        </Row>
      </Card>
    </React.Fragment>);
};

export default FilterDropdownComponent;
